/**
 * KAN-910: период CRM в адресе. Относительный период пишется именем, а не датами
 * (?period=today|yesterday|7d|30d|all), чтобы ссылка с today, открытая завтра,
 * показывала завтрашний день. Конкретные даты (?from=&to=) только при выборе дней
 * в календаре. Одни правила для таблицы (period/from/to), "Лиды по дням" (те же
 * параметры) и доски (activity/activity_from/activity_to).
 *
 * Разбор: известное имя главнее дат; нет имени - даты (старые ссылки с from/to
 * продолжают работать); нет ничего - умолчание вызывающего.
 */
import type { CrmRange } from './crmTable';
import { parseCrmRange, todayMoscow } from './crmTable';
import { PERIOD_PRESETS, periodPresetRange, type PeriodChoice, type PeriodPreset } from './periodPresets';

/** Имена параметров одного периода в адресе. */
export interface UrlPeriodKeys {
  period: string;
  from: string;
  to: string;
}

/** Имя быстрого периода в адресе (короткое и стабильное). */
export const PERIOD_URL_NAME: Record<PeriodPreset, string> = {
  today: 'today',
  yesterday: 'yesterday',
  week: '7d',
  month: '30d',
};

export const PERIOD_URL_ALL = 'all';

/** Значение периода: быстрый пункт, "Всё время" или конкретные даты. */
export type UrlPeriodValue = PeriodChoice | CrmRange;

/** Пункт по имени из адреса; неизвестное имя - null. */
export function parsePeriodName(value: string | null): PeriodChoice | null {
  if (value === PERIOD_URL_ALL) return 'all';
  return PERIOD_PRESETS.find((preset) => PERIOD_URL_NAME[preset] === value) ?? null;
}

/** Имя пункта для адреса. */
export function periodChoiceUrlName(choice: PeriodChoice): string {
  return choice === 'all' ? PERIOD_URL_ALL : PERIOD_URL_NAME[choice];
}

export interface ParsedUrlPeriod {
  /** null - всё время. */
  range: CrmRange | null;
  /** Пункт, если период задан именем (или умолчанием-именем); даты - null. */
  choice: PeriodChoice | null;
}

/**
 * Период из адреса. allowAll false: ?period=all не понимается (вид без "Всё время"),
 * тогда действует fallback. fallback - пункт по умолчанию, если в адресе ни имени, ни дат.
 */
export function parseUrlPeriod(
  params: URLSearchParams,
  keys: UrlPeriodKeys,
  fallback: PeriodChoice,
  options: { allowAll?: boolean; now?: Date } = {}
): ParsedUrlPeriod {
  const { allowAll = true, now = new Date() } = options;
  const named = parsePeriodName(params.get(keys.period));
  const usable = named === 'all' && !allowAll ? null : named;
  if (usable) return fromChoice(usable, now);
  const from = params.get(keys.from);
  const to = params.get(keys.to);
  if (from || to) return { range: parseCrmRange(from, to, now), choice: null };
  return fromChoice(fallback, now);
}

function fromChoice(choice: PeriodChoice, now: Date): ParsedUrlPeriod {
  if (choice === 'all') return { range: null, choice };
  return { range: periodPresetRange(choice, todayMoscow(now)), choice };
}

/** Адрес с периодом: пункт - ?period=<имя> без дат, даты - ?from=&to= без имени. */
export function withUrlPeriod(prev: URLSearchParams, keys: UrlPeriodKeys, value: UrlPeriodValue): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (typeof value === 'string') {
    next.set(keys.period, periodChoiceUrlName(value));
    next.delete(keys.from);
    next.delete(keys.to);
  } else {
    next.set(keys.from, value.from);
    next.set(keys.to, value.to);
    next.delete(keys.period);
  }
  return next;
}

/** В адресе уже задан период (имя или даты): умолчание не пишется. */
export function hasUrlPeriod(params: URLSearchParams, keys: UrlPeriodKeys): boolean {
  return !!(params.get(keys.period) || params.get(keys.from) || params.get(keys.to));
}
